import React from 'react';
import { Link } from 'react-router-dom';
import { useAppSelector } from '../../hooks/redux';
import KYCStatusBadge from './KYCStatusBadge';
import Button from '../UI/Button';

const KYCNotification: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const { status, expired, expirationDate, requiresKYC } = useAppSelector((state) => state.kyc);

  // Don't show if KYC is approved and not expired
  if (status === 'approved' && !expired && !requiresKYC) {
    return null;
  }

  // Calculate days until expiration
  const daysUntilExpiration = expirationDate && !expired
    ? Math.ceil((new Date(expirationDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
    : null;

  const isExpiringSoon = daysUntilExpiration !== null && daysUntilExpiration <= 30 && daysUntilExpiration > 0;

  return (
    <div className={`
      p-4 rounded-lg border mb-6
      ${expired
        ? 'bg-[var(--color-error-50)] border-[var(--color-error-200)]'
        : isExpiringSoon
        ? 'bg-[var(--color-warning-50)] border-[var(--color-warning-200)]'
        : status === 'none' || requiresKYC
        ? 'bg-[var(--color-brand-50)] border-[var(--color-brand-200)]'
        : 'bg-[var(--color-neutral-50)] border-[var(--color-neutral-border)]'
      }
    `}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-semibold text-[var(--color-default-font)]">
              {expired
                ? 'KYC Verification Expired'
                : isExpiringSoon
                ? 'KYC Verification Expiring Soon'
                : status === 'none' || requiresKYC
                ? 'KYC Verification Required'
                : 'KYC Status'}
            </h3>
            <KYCStatusBadge status={status} citizenStatus={user?.kyc?.citizenStatus} />
          </div>
          
          <p className={`
            text-sm mb-2
            ${expired
              ? 'text-[var(--color-error-700)]'
              : isExpiringSoon
              ? 'text-[var(--color-warning-700)]'
              : 'text-[var(--color-subtext-color)]'
            }
          `}>
            {expired
              ? 'Your KYC verification has expired. Please complete the verification process again to maintain your citizen status.'
              : isExpiringSoon
              ? `Your KYC verification will expire in ${daysUntilExpiration} day${daysUntilExpiration !== 1 ? 's' : ''}. Please update your documents before expiration.`
              : status === 'none' || requiresKYC
              ? 'Complete KYC verification to unlock full platform features and become a verified citizen.'
              : status === 'pending'
              ? 'Your KYC submission is under review. You will be notified once the review is complete.'
              : status === 'rejected'
              ? 'Your KYC submission was rejected. Please review the rejection reason and resubmit.'
              : 'Your KYC verification is active.'}
          </p>

          {expirationDate && !expired && (
            <p className="text-xs text-[var(--color-subtext-color)]">
              Expires: {new Date(expirationDate).toLocaleDateString()}
            </p>
          )}
        </div>

        <Link to="/kyc">
          <Button
            variant={expired || requiresKYC ? 'primary' : 'outline'}
            size="sm"
          >
            {expired || requiresKYC
              ? 'Start KYC'
              : status === 'pending'
              ? 'View Status'
              : 'Update KYC'}
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default KYCNotification;

